import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Alert,
    Badge,
    Button,
    Card,
    Container,
    Divider,
    Group,
    Loader,
    Paper,
    Select,
    Stack,
    Text,
    TextInput,
    Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
    IconAlertCircle,
    IconBed,
    IconCheck,
    IconDeviceFloppy,
    IconUsers,
} from "@tabler/icons-react";

import HeroSectionV2 from "../../components/HeroSectionV2";
import Billede1 from "../../Images/Billede1.jpg";
import {
    getHotelBooking,
    HotelBookingDto,
    updateHotelBookingGuests,
} from "../../api/hotelBookingApi";
import { useLanguage } from "../../context/LanguageContext";

type GuestForm = {
    personName: string;
    role: string;
};

type RoomGuestForm = {
    hotelBookingRoomID: number;
    guests: GuestForm[];
};

export default function HotelRoomingListPage() {
    const apiUrl = process.env.REACT_APP_API_URL as string;
    const { language, setLanguage } = useLanguage();
    const { bookingId } = useParams();
    const navigate = useNavigate();

    const parsedBookingId = Number(bookingId);

    const [booking, setBooking] = useState<HotelBookingDto | null>(null);
    const [rooms, setRooms] = useState<RoomGuestForm[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setLanguage("en");
    }, [setLanguage]);

    const texts = {
        en: {
            heroTitle: "Rooming List",
            heroDesc: "Fill in who will stay in each hotel room.",
            booking: "Booking",
            hotel: "Hotel",
            status: "Status",
            roomingList: "Rooming list",
            roomingText: "Add the guests for each room below.",
            room: "Room",
            roomType: "Room type",
            dates: "Dates",
            guest: "Guest",
            guestName: "Guest name",
            role: "Role",
            email: "Email",
            phone: "Phone",
            save: "Save rooming list",
            saved: "Saved",
            savedMessage: "Rooming list has been saved",
            error: "Error",
            cannotLoad: "Could not load booking",
            cannotSave: "Could not save rooming list",
            notAllowed: "Rooming list is only available after payment.",
            player: "Player",
            coach: "Coach",
            staff: "Staff",
            other: "Other",
            totalRooms: "Rooms",
            totalGuests: "Guests",
            deadlinePassed: "The rooming list deadline has passed. You can view the information, but no longer edit it.",
            deadlineOpen: "Rooming list can still be edited.",
            back: "Back"
        },
        da: {
            heroTitle: "Rooming List",
            heroDesc: "Udfyld hvem der skal bo på hvert hotelværelse.",
            booking: "Booking",
            hotel: "Hotel",
            status: "Status",
            roomingList: "Rooming list",
            roomingText: "Tilføj gæsterne for hvert værelse nedenfor.",
            room: "Værelse",
            roomType: "Værelsestype",
            dates: "Datoer",
            guest: "Gæst",
            guestName: "Navn på gæst",
            role: "Rolle",
            email: "Email",
            phone: "Telefon",
            save: "Gem rooming list",
            saved: "Gemt",
            savedMessage: "Rooming list er gemt",
            error: "Fejl",
            cannotLoad: "Kunne ikke hente booking",
            cannotSave: "Kunne ikke gemme rooming list",
            notAllowed: "Rooming list er først tilgængelig efter betaling.",
            player: "Spiller",
            coach: "Coach",
            staff: "Staff",
            other: "Andet",
            totalRooms: "Værelser",
            totalGuests: "Gæster",
            deadlinePassed: "Deadline for rooming list er overskredet. Du kan se oplysningerne, men ikke længere rette dem.",
            deadlineOpen: "Rooming list kan stadig redigeres.",
            back: "Tilbage"
        },
    };

    const t = texts[language];

    const totalGuests = useMemo(() => {
        return rooms.reduce((sum, room) => sum + room.guests.length, 0);
    }, [rooms]);

    const roomingDeadlinePassed = useMemo(() => {
        if (!booking?.roomingDeadline) return false;
        return new Date() > new Date(booking.roomingDeadline);
    }, [booking]);

    const canEdit = useMemo(() => {
        if (!booking) return false;

        const status = normalizeStatus(booking.status);
        const statusAllowsEdit = status === "paid" || status === "roomassignmentopen";

        return statusAllowsEdit && !roomingDeadlinePassed;
    }, [booking, roomingDeadlinePassed]);

    useEffect(() => {
        loadBooking();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiUrl, parsedBookingId]);

    function normalizeStatus(status?: string | null) {
        return (status || "").trim().replace(/\s+/g, "").toLowerCase();
    }

    function formatDate(value?: string | null) {
        if (!value) return "-";
        return new Date(value).toLocaleDateString("da-DK");
    }

    function getStatusColor(status: string) {
        switch (normalizeStatus(status)) {
            case "pendingapproval":
                return "yellow";
            case "awaitingpayment":
                return "blue";
            case "paid":
                return "green";
            case "roomassignmentopen":
                return "violet";
            case "completed":
                return "green";
            case "rejected":
                return "red";
            default:
                return "gray";
        }
    }

    function getStatusText(status: string) {
        const isEn = language === "en";

        switch (normalizeStatus(status)) {
            case "pendingapproval":
                return isEn ? "Pending approval" : "Afventer godkendelse";
            case "awaitingpayment":
                return isEn ? "Awaiting payment" : "Afventer betaling";
            case "paid":
                return isEn ? "Paid" : "Betalt";
            case "roomassignmentopen":
                return isEn ? "Rooming list open" : "Rooming list åben";
            case "completed":
                return isEn ? "Completed" : "Afsluttet";
            case "rejected":
                return isEn ? "Rejected" : "Afvist";
            default:
                return status;
        }
    }

    async function loadBooking() {
        if (!parsedBookingId || parsedBookingId <= 0) return;

        setLoading(true);

        try {
            const data = await getHotelBooking(apiUrl, parsedBookingId);
            setBooking(data);

            const mappedRooms: RoomGuestForm[] = data.rooms.map((room) => {
                const existingGuests = room.guests ?? [];

                const guestCount = Math.max(room.guestsCount || 1, existingGuests.length || 0);

                const guests: GuestForm[] = Array.from({ length: guestCount }).map((_, index) => {
                    const existing = existingGuests[index];

                    return {
                        personName: existing?.personName ?? "",
                        role: existing?.role ?? "Player",
                    };
                });

                return {
                    hotelBookingRoomID: room.hotelBookingRoomID,
                    guests,
                };
            });

            setRooms(mappedRooms);
        } catch (e: any) {
            notifications.show({
                color: "red",
                title: t.error,
                message: e?.message || t.cannotLoad,
            });
        } finally {
            setLoading(false);
        }
    }

    function updateGuest(
        hotelBookingRoomID: number,
        guestIndex: number,
        patch: Partial<GuestForm>
    ) {
        setRooms((prev) =>
            prev.map((room) => {
                if (room.hotelBookingRoomID !== hotelBookingRoomID) return room;

                return {
                    ...room,
                    guests: room.guests.map((guest, index) =>
                        index === guestIndex ? { ...guest, ...patch } : guest
                    ),
                };
            })
        );
    }

    async function saveRoomingList() {
        if (!booking) return;

        if (!canEdit) {
            notifications.show({
                color: "yellow",
                title: t.status,
                message: t.notAllowed,
            });
            return;
        }

        setSaving(true);

        try {
            await updateHotelBookingGuests(apiUrl, booking.hotelBookingID, {
                rooms: rooms.map((room) => ({
                    hotelBookingRoomID: room.hotelBookingRoomID,
                    guests: room.guests.map((guest) => ({
                        personName: guest.personName || null,
                        role: guest.role || null,
                        email: null,
                        phone: null,
                    })),
                })),
            });

            notifications.show({
                color: "green",
                title: t.saved,
                message: t.savedMessage,
            });

            await loadBooking();
        } catch (e: any) {
            notifications.show({
                color: "red",
                title: t.error,
                message: e?.message || t.cannotSave,
            });
        } finally {
            setSaving(false);
        }
    }

    function getBookingRoom(roomId: number) {
        return booking?.rooms.find((x) => x.hotelBookingRoomID === roomId);
    }

    return (
        <div>
            <HeroSectionV2
                titleEn={texts.en.heroTitle}
                titleDa={texts.da.heroTitle}
                descriptionEn={texts.en.heroDesc}
                descriptionDa={texts.da.heroDesc}
                imageSrc={Billede1}
                badgeText="HOTELS"
            />



            <Container size="xl" py="md">
                <Stack gap="md">
                    {loading ? (
                        <Paper withBorder p="xl" radius="md">
                            <Group justify="center">
                                <Loader />
                            </Group>
                        </Paper>
                    ) : !booking ? (
                        <Alert color="red" icon={<IconAlertCircle size={18} />}>
                            {t.cannotLoad}
                        </Alert>
                    ) : (
                        <>
                                    <Paper withBorder p="md" radius="md">
                                        <Group justify="space-between" align="center" wrap="nowrap">
                                            <Stack gap={2}>
                                                <Group gap="xs">
                                                    <Title order={3}>
                                                        {t.booking} #{booking.hotelBookingID}
                                                    </Title>

                                                    <Badge color="red" variant="filled" radius="xl">
                                                        {getStatusText(booking.status)}
                                                    </Badge>
                                                </Group>

                                                <Text c="dimmed" size="sm">
                                                    {t.hotel}: <strong>{booking.hotelName || booking.hotelID}</strong>
                                                </Text>
                                            </Stack>

                                            <Group gap="xs" wrap="nowrap">
                                                <Button
                                                    variant="light"
                                                    color="red"
                                                    size="sm"
                                                    onClick={() => navigate(-1)}
                                                >
                                                    ← {t.back}
                                                </Button>

                                                <Badge
                                                    color="red"
                                                    variant="light"
                                                    size="lg"
                                                    leftSection={<IconBed size={14} />}
                                                >
                                                    {t.totalRooms}: {booking.rooms.length}
                                                </Badge>

                                                <Badge
                                                    color="red"
                                                    variant="light"
                                                    size="lg"
                                                    leftSection={<IconUsers size={14} />}
                                                >
                                                    {t.totalGuests}: {totalGuests}
                                                </Badge>
                                            </Group>
                                        </Group>
                                    </Paper>

                                    {booking.roomingDeadline && (
                                        <Alert
                                            color={roomingDeadlinePassed ? "yellow" : "blue"}
                                            icon={<IconAlertCircle size={18} />}
                                        >
                                            {roomingDeadlinePassed
                                                ? `${t.deadlinePassed} (${formatDate(booking.roomingDeadline)})`
                                                : `${t.deadlineOpen} ${formatDate(booking.roomingDeadline)}`}
                                        </Alert>
                                    )}

                                    {!canEdit && !roomingDeadlinePassed && (
                                        <Alert color="yellow" icon={<IconAlertCircle size={18} />}>
                                            {t.notAllowed}
                                        </Alert>
                                    )}

                            <Paper withBorder p="lg" radius="md">
                                <Stack gap={2}>
                                    <Title order={4}>{t.roomingList}</Title>
                                    <Text c="dimmed" size="sm">
                                        {t.roomingText}
                                    </Text>
                                </Stack>
                            </Paper>

                            {rooms.map((roomForm, roomIndex) => {
                                const room = getBookingRoom(roomForm.hotelBookingRoomID);

                                return (
                                    <Card key={roomForm.hotelBookingRoomID} withBorder radius="md" p="lg">
                                        <Stack gap="md">
                                            <Group justify="space-between" align="flex-start">
                                                <Stack gap={3}>
                                                    <Title order={4}>
                                                        {t.room} {roomIndex + 1}
                                                    </Title>

                                                    <Group gap="lg">
                                                        <Text size="sm">
                                                            {t.roomType}:{" "}
                                                            <strong>{room?.roomTypeName || "-"}</strong>
                                                        </Text>

                                                        <Text size="sm">
                                                            {t.dates}:{" "}
                                                            <strong>
                                                                {formatDate(room?.checkInDate)} →{" "}
                                                                {formatDate(room?.checkOutDate)}
                                                            </strong>
                                                        </Text>
                                                    </Group>
                                                </Stack>

                                                <Badge variant="light">
                                                    {roomForm.guests.length} {t.guest}
                                                </Badge>
                                            </Group>

                                            <Divider />

                                            {roomForm.guests.map((guest, guestIndex) => (
                                                <Paper
                                                    key={`${roomForm.hotelBookingRoomID}-${guestIndex}`}
                                                    withBorder
                                                    p="md"
                                                    radius="md"
                                                    bg="gray.0"
                                                >
                                                    <Stack gap="sm">
                                                        <Group>
                                                            <IconUsers size={18} />
                                                            <Text fw={700}>
                                                                {t.guest} {guestIndex + 1}
                                                            </Text>
                                                        </Group>

                                                        <Group grow>
                                                            <TextInput
                                                                label={t.guestName}
                                                                value={guest.personName}
                                                                onChange={(e) =>
                                                                    updateGuest(
                                                                        roomForm.hotelBookingRoomID,
                                                                        guestIndex,
                                                                        { personName: e.currentTarget.value }
                                                                    )
                                                                }
                                                                disabled={!canEdit}
                                                            />

                                                            <Select
                                                                label={t.role}
                                                                value={guest.role}
                                                                onChange={(v) =>
                                                                    updateGuest(
                                                                        roomForm.hotelBookingRoomID,
                                                                        guestIndex,
                                                                        { role: v || "Player" }
                                                                    )
                                                                }
                                                                data={[
                                                                    { value: "Player", label: t.player },
                                                                    { value: "Coach", label: t.coach },
                                                                    { value: "Staff", label: t.staff },
                                                                    { value: "Other", label: t.other },
                                                                ]}
                                                                disabled={!canEdit}
                                                            />
                                                        </Group>
                                                    </Stack>
                                                </Paper>
                                            ))}
                                        </Stack>
                                    </Card>
                                );
                            })}

                            <Paper withBorder p="lg" radius="md">
                                <Group justify="space-between">
                                    <Text c="dimmed" size="sm">
                                        {canEdit
                                            ? t.roomingText
                                            : t.notAllowed}
                                    </Text>

                                    <Button
                                        color="green"
                                        loading={saving}
                                        disabled={!canEdit}
                                        onClick={saveRoomingList}
                                        leftSection={<IconDeviceFloppy size={16} />}
                                    >
                                        {t.save}
                                    </Button>
                                </Group>
                            </Paper>
                        </>
                    )}
                </Stack>
            </Container>
        </div>
    );
}